import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Input, Label, TextField } from "react-aria-components";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Abweisung, Status } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { Scrollbereich } from "../design/Scrollbereich";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl, umschlag } from "../plattform/api";
import { useDokument, useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useNetz } from "../plattform/Offline";
import { useServerkontakt } from "../plattform/serverzeit";
import { useSitzung } from "../plattform/sitzung";
import { beobachten, einreihen, eintraege, type Eintrag } from "../plattform/warteschlange";
import {
  anhangText, bezeichnungAllein, kanalname, personText, SYSTEMMELDUNG_FELDER, SYSTEMMELDUNG_SYMBOL, uhrzeit,
} from "./darstellung";
import type { Rahmen } from "./Kanaele";
import { abrufadresse } from "./medien";
import { mediumPfad } from "./MediumOeffnen";
import { Mitgliedsreviere } from "./Mitgliedsreviere";

/**
 * Kanal (oberflaeche.md §3.1, §7): `region` „Verlauf“ mit einem `feed` (Name = Kanalname), darin Nachrichten und
 * Systemmeldungen als `article` in der Reihenfolge, in der der Server sie angenommen hat (`_reihenfolge`, K16). Texte
 * stehen immer als Text, nie als Auszeichnung (ADR-0019). Eigene Nachrichten gehen über die Warteschlange (ADR-0020):
 * sie stehen sofort mit ihrem Übertragungsstand im Verlauf und erscheinen genau einmal, sobald der Server sie hat.
 * Eingehende Nachrichten sind lautlos (F-215). Medien lädt die App erst auf Antippen, beim Öffnen nur Vorschaubilder
 * (F-227); ohne Netz zeigt ein Anhang den Hinweis `<Medienart> nur mit Netz`.
 */

const BEFEHL = "NachrichtSchreiben";
type Medienart = "Foto" | "Video" | "Dokument" | "Sprachnachricht";
const ANNAHME: Record<string, string> = { Foto: "image/*", Video: "video/*", Dokument: "application/pdf,*/*" };

function useWartende(kanalId: string): Eintrag[] {
  const [liste, setListe] = useState<Eintrag[]>([]);
  useEffect(() => {
    const lesen = () => void eintraege().then((e) => setListe(e.filter((x) => x.befehl === BEFEHL && x.daten["Kanal"] === kanalId)));
    lesen();
    return beobachten(lesen);
  }, [kanalId]);
  return liste;
}

function Systemmeldung({ z }: { z: Zeile }) {
  const id = useId();
  const art = String(z["Art"] ?? "");
  const felder = SYSTEMMELDUNG_FELDER[art] ?? [];
  const symbol = art === "Jagdzustand gewechselt" && z["Jagdzustand"] === "Jagd ruht" ? "hahn-in-ruh" : SYSTEMMELDUNG_SYMBOL[art] ?? "info";
  return (
    <article className="msg msg--system" aria-labelledby={id}>
      <div className="msg__kopf">
        <Symbol name={symbol} />
        <span className="msg__wer" id={id}>{art}</span>
        <span className="msg__zeit zeitstempel">{uhrzeit(z["Zeitpunkt"])}</span>
      </div>
      <dl className="msg__angaben">
        {felder.map((f) => {
          const wert = f === "Person" ? personText(z, "Person") : z[f] === null || z[f] === undefined ? null : String(z[f]);
          if (wert === null || wert === "") {
            return f === "Stand" && (art === "Notruf" || art === "Erlegung") ? <div key={f}><dt>{f}</dt><dd>—</dd></div> : null;
          }
          return <div key={f}><dt>{f}</dt><dd>{wert}</dd></div>;
        })}
      </dl>
    </article>
  );
}

/** Anhang einer sichtbaren Nachricht (§7): Foto/Video mit Vorschaubild, Sprachnachricht, Dokument, Standort. */
function Anhang({ z, kanalId, online }: { z: Zeile; kanalId: string; online: boolean }) {
  const a = (z["Anhang"] ?? null) as Record<string, unknown> | null;
  const art = String(a?.["Medienart"] ?? "");
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [spielt, setSpielt] = useState(false);
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if ((art === "Foto" || art === "Video") && online && !vorschau) void abrufadresse(kanalId, z._id, "vorschau").then(setVorschau);
  }, [art, online, kanalId, z._id, vorschau]);
  useEffect(() => () => { audio.current?.pause(); }, []);
  if (art === "Standort") {
    const p = z["Position"] as { Breite?: number; Länge?: number } | null;
    if (!p || typeof p.Breite !== "number" || typeof p.Länge !== "number") return null;
    const ziel = `geo:${p.Breite.toFixed(6)},${p.Länge.toFixed(6)}`;
    return (
      <div className="msg__anhang">
        <span className="zeitstempel">{`${p.Breite.toFixed(6)}, ${p.Länge.toFixed(6)}`}</span>
        <Kachel symbol="navigation" begriff="Navigation" art="kompakt" onPress={() => { window.open(ziel, "_blank"); }} />
      </div>
    );
  }
  if (!["Foto", "Video", "Sprachnachricht", "Dokument"].includes(art)) return null;
  if (!online) return <Status symbol="offline">{`${art} nur mit Netz`}</Status>;
  /** Übergibt das Medium an den Systembetrachter: sofort eine eigene Seite, die die Leseadresse holt (MediumOeffnen). */
  function oeffnen() {
    const pfad = mediumPfad(kanalId, z._id, art);
    if (pfad) window.open(pfad, "_blank", "noopener");
  }
  async function abspielen() {
    if (spielt) {
      audio.current?.pause();
      setSpielt(false);
      return;
    }
    const url = await abrufadresse(kanalId, z._id, "medium");
    const el = audio.current;
    if (!url || !el) return;
    el.src = url;
    el.onended = () => setSpielt(false);
    setSpielt(true);
    await el.play().catch(() => setSpielt(false));
  }
  if (art === "Sprachnachricht") {
    return (
      <div className="sprach">
        <Kachel symbol={spielt ? "anhalten" : "abspielen"} begriff={spielt ? "Anhalten" : "Abspielen"} art="kompakt" onPress={() => void abspielen()} />
        <span>{`${String(a?.["Dauer"] ?? "")} s`}</span>
        {spielt ? <Status symbol="info">Kopfhörer empfohlen</Status> : null}
        <audio ref={audio} preload="none" hidden />
      </div>
    );
  }
  return (
    <div className="msg__anhang">
      {vorschau ? <img className="msg__vorschau" src={vorschau} alt="" /> : null}
      <Kachel symbol={art === "Dokument" ? "dokument" : art === "Video" ? "video" : "foto"} begriff={art} art="kompakt" onPress={oeffnen} />
      {art === "Video" && a?.["Dauer"] ? <span>{`${String(a["Dauer"])} s`}</span> : null}
      {art === "Dokument" && a?.["Dateiname"] ? <span>{String(a["Dateiname"])}</span> : null}
    </div>
  );
}

/**
 * Anhang, dessen Medium (noch) nicht abrufbar ist (in Aufbereitung, abgelehnt, noch nicht übertragen): Art und Merkmale
 * als Text; eine Sprachnachricht behält ihre Form mit „Abspielen“, das erst mit dem freigegebenen Medium bedienbar wird.
 */
function AnhangOhneMedium({ z, text }: { z: Zeile; text: string }) {
  const a = (z["Anhang"] ?? null) as Record<string, unknown> | null;
  if (a?.["Medienart"] === "Sprachnachricht") {
    return (
      <div className="sprach">
        <Kachel symbol="abspielen" begriff="Abspielen" art="kompakt" isDisabled onPress={() => undefined} />
        <span>{text}</span>
      </div>
    );
  }
  return <span className="msg__anhang"><Symbol name="anhang" />{text}</span>;
}

/**
 * Eine Nachricht im Verlauf. `uebertragung` ist der Übertragungsstand einer auf diesem Gerät geschriebenen Nachricht
 * (oberflaeche.md §10, TESTSPRACHE §3: „noch nicht übertragen“, „übertragen“, „abgewiesen“ mit Grund); `lokal` heißt,
 * die Nachricht steht nur in der Warteschlange und noch nicht beim Server.
 */
/**
 * Anhang einer Nachricht, die noch in der Warteschlange liegt: Die Datei ist nur auf diesem Gerät; „Foto“, „Video“,
 * „Dokument“ öffnen sie dort, „Abspielen“ spielt die Aufnahme ab (oberflaeche.md §7, gleiche Bedienelemente wie später).
 */
function AnhangLokal({ z, datei }: { z: Zeile; datei: Blob }) {
  const art = String(((z["Anhang"] ?? {}) as Record<string, unknown>)["Medienart"] ?? "");
  const [url, setUrl] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(datei);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [datei]);
  if (art === "Sprachnachricht") {
    return (
      <div className="sprach">
        <Kachel symbol="abspielen" begriff="Abspielen" art="kompakt"
          onPress={() => { const el = audio.current; if (el && url) { el.src = url; void el.play().catch(() => undefined); } }} />
        <span>Sprachnachricht</span>
        <audio ref={audio} preload="none" hidden />
      </div>
    );
  }
  return (
    <div className="msg__anhang">
      {art === "Foto" && url ? <img className="msg__vorschau" src={url} alt="" /> : null}
      <Kachel symbol={art === "Dokument" ? "dokument" : art === "Video" ? "video" : "foto"} begriff={art} art="kompakt"
        onPress={() => { if (url) window.open(url, "_blank", "noopener"); }} />
    </div>
  );
}

function Nachricht({ z, kanalId, online, eigen, darfEntfernen, onEntfernen, uebertragung, lokal = false, datei = null }: {
  z: Zeile; kanalId: string; online: boolean; eigen: boolean; darfEntfernen: boolean; onEntfernen: () => void;
  uebertragung?: { stand: string; grund: string | null } | null; lokal?: boolean; datei?: Blob | null;
}) {
  const status = String(z["Status"] ?? "sichtbar");
  const zurueckgezogen = status === "zurückgezogen";
  const bez = bezeichnungAllein(z, "Verfasser");
  const text = typeof z["Text"] === "string" && z["Text"] !== "" ? z["Text"] : null;
  const anhang = anhangText(z["Anhang"]);
  return (
    <article className={`msg${eigen ? " msg--eigen" : ""}`}>
      <div className="msg__kopf">
        <span className="msg__wer">{personText(z, "Verfasser")}</span>
        {bez ? <span className="msg__bez">{bez}</span> : null}
        <span className="msg__zeit zeitstempel">{uhrzeit(z["Zeitpunkt"])}</span>
      </div>
      {text && !zurueckgezogen ? <p className="msg__b">{text}</p> : null}
      {!zurueckgezogen && status === "sichtbar" && z["Anhang"] && !lokal ? <Anhang z={z} kanalId={kanalId} online={online} /> : null}
      {!zurueckgezogen && lokal && datei && anhang ? <AnhangLokal z={z} datei={datei} /> : null}
      {!zurueckgezogen && (status !== "sichtbar" || lokal) && !(lokal && datei) && anhang ? <AnhangOhneMedium z={z} text={anhang} /> : null}
      {status !== "sichtbar" && !lokal ? (
        <span className="msg__status"><span>{status}</span>{z["Grund"] ? <> <span className="grund">{String(z["Grund"])}</span></> : null}</span>
      ) : null}
      {uebertragung ? (
        <span className="msg__status"><span>{uebertragung.stand}</span>{uebertragung.grund ? <> <span className="grund">{uebertragung.grund}</span></> : null}</span>
      ) : null}
      {darfEntfernen && !zurueckgezogen ? <Kachel symbol="schliessen" begriff="Entfernen" art="kompakt" onPress={onEntfernen} /> : null}
    </article>
  );
}

interface Vorgemerkt { medienart: Medienart; datei: Blob; name: string }

function parsePosition(t: string): { Breite: number; Länge: number } | null {
  const m = /^\s*(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)\s*$/.exec(t);
  if (!m) return null;
  return { Breite: Number(m[1]!.replace(",", ".")), Länge: Number(m[2]!.replace(",", ".")) };
}

export function Kanal({ rahmen }: { rahmen?: Rahmen }) {
  const { kanalId = "" } = useParams();
  const [suche] = useSearchParams();
  const dj = suche.get("drueckjagd");
  const navigate = useNavigate();
  const zurueckZuDenKanaelen = () => navigate(dj ? `/kanaele?drueckjagd=${dj}` : "/kanaele");
  const online = useNetz();
  const { uid } = useSitzung();
  const kanal = useDokument(kanalId ? `kanaele/${kanalId}` : null);
  const nachrichten = useSammlung(kanalId ? `kanaele/${kanalId}/nachrichten` : null);
  const ich = useDokument(kanalId && uid ? `kanaele/${kanalId}/mitglieder/${uid}` : null);
  const wartende = useWartende(kanalId);
  const kontakt = useServerkontakt();
  const [text, setText] = useState("");
  const [vorgemerkt, setVorgemerkt] = useState<Vorgemerkt | null>(null);
  const [anhangOffen, setAnhangOffen] = useState(false);
  // Handlungen der Eingabe hinter „mehr“ auf niedriger Höhe (Anforderungen K101 (4), gestaltung.ts `niedrigeHoehe`)
  const [mehrOffen, setMehrOffen] = useState(false);
  const [standortOffen, setStandortOffen] = useState(false);
  const [position, setPosition] = useState<{ Breite: number; Länge: number; Genauigkeit: number | null } | null>(null);
  const [positionText, setPositionText] = useState("");
  const [karte, setKarte] = useState(false);
  const [aufnahme, setAufnahme] = useState<MediaRecorder | null>(null);
  const [entfernen, setEntfernen] = useState<string | null>(null);
  const [abweisung, setAbweisung] = useState<string | null>(null);
  const verlaufRef = useRef<HTMLDivElement>(null);
  const dateiwahl = useRef<HTMLInputElement>(null);
  const gewaehlteArt = useRef<Medienart>("Foto");

  const meine = (ich.daten?.["Mitglied"] as { Kennung?: string } | undefined)?.Kennung ?? null;
  const leitung = ich.daten?.["_leitung"] === true;
  const name = kanal.daten ? kanalname(kanal.daten) : "Kanal";

  const vorhandene = useMemo(() => new Set(nachrichten.daten.map((n) => n._id)), [nachrichten.daten]);
  const offen = wartende.filter((w) => !vorhandene.has(w.meldungsId));
  // Auf diesem Gerät geschriebene Nachrichten, die der Server schon hat: Übertragungsstand „übertragen“
  const hierGeschrieben = useMemo(() => new Set(wartende.map((w) => w.meldungsId)), [wartende]);

  useEffect(() => {
    const el = verlaufRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [nachrichten.daten.length, offen.length]);

  async function senden(zusatz?: Record<string, unknown>) {
    const t = text;
    const anhang = vorgemerkt;
    if (!t.trim() && !anhang && !zusatz) return;
    setText("");
    setVorgemerkt(null);
    const daten: Record<string, unknown> = { Kanal: kanalId, ...(t.trim() ? { Text: t } : {}) };
    if (zusatz) daten["Anhang"] = zusatz;
    else if (anhang) daten["Anhang"] = { Medienart: anhang.medienart };
    await einreihen(BEFEHL, "Nachricht", umschlag(daten), zusatz ? undefined : anhang?.datei);
  }

  function dateiWaehlen(art: Medienart) {
    gewaehlteArt.current = art;
    setAnhangOffen(false);
    const el = dateiwahl.current;
    if (!el) return;
    el.accept = ANNAHME[art] ?? "*/*";
    el.value = "";
    el.click();
  }

  async function aufnehmen() {
    try {
      const strom = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(strom);
      const teile: Blob[] = [];
      r.ondataavailable = (e) => { if (e.data.size) teile.push(e.data); };
      r.onstop = () => {
        strom.getTracks().forEach((s) => s.stop());
        const datei = new File(teile, "sprachnachricht.webm", { type: r.mimeType || "audio/webm" });
        setVorgemerkt({ medienart: "Sprachnachricht", datei, name: "Sprachnachricht" });
      };
      r.start();
      setAufnahme(r);
    } catch {
      setAbweisung("aufnahme-nicht-moeglich");
    }
  }

  function gpsMessung() {
    setKarte(false);
    navigator.geolocation.getCurrentPosition(
      (p) => setPosition({ Breite: p.coords.latitude, Länge: p.coords.longitude, Genauigkeit: Math.round(p.coords.accuracy) }),
      () => setAbweisung("keine-position"), { enableHighAccuracy: true, timeout: 30_000 });
  }

  async function standortSenden() {
    const p = karte ? parsePosition(positionText) : position;
    if (!p) return;
    setStandortOffen(false);
    setPosition(null);
    setPositionText("");
    await senden({ Medienart: "Standort", Position: { Breite: p.Breite, Länge: p.Länge },
      ...("Genauigkeit" in p && p.Genauigkeit !== null ? { Genauigkeit: p.Genauigkeit } : {}) });
  }

  async function zurueckziehen(nid: string) {
    setEntfernen(null);
    const a = await befehl("NachrichtZurueckziehen", { Kanal: kanalId, Nachricht: nid });
    setAbweisung(abweisungsgrund(a));
  }

  const verfasser = (z: Zeile) => (z["Verfasser"] as { Kennung?: string } | null)?.Kennung ?? null;
  const textfeld = (
    <TextField className="feld-text eingabe__text" value={text} onChange={setText}>
      <Label className="feld-text__b">Nachricht</Label>
      <Input onKeyDown={(e) => { if (e.key === "Enter" && !standortOffen) void senden(); }} />
    </TextField>
  );

  // Referenz `#s-kanal` (design/README §9.11): Verlauf unten verankert mit „▴ n ältere“, darunter die Eingabe
  const jagdherrenkanal = kanal.daten?.["Kanalart"] === "Jagdherrenkanal";
  const eigenesRevier = (ich.daten?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null;
  const inhalt = (
    <>
      {jagdherrenkanal && kanalId ? <Mitgliedsreviere kanalId={kanalId} eigenesRevier={eigenesRevier} verwaltend={leitung} /> : null}
      <div className="verlauf" role="region" aria-label="Verlauf">
        <Scrollbereich auf role="feed" aria-label={name} aria-busy={nachrichten.laedt} scrollRef={verlaufRef}>
            {nachrichten.daten.map((z) => z["Nachrichtenart"] === "Systemmeldung"
              ? <Systemmeldung key={z._id} z={z} />
              : (
                <Nachricht key={z._id} z={z} kanalId={kanalId} online={online} eigen={!!meine && verfasser(z) === meine}
                  darfEntfernen={leitung || (!!meine && verfasser(z) === meine)} onEntfernen={() => setEntfernen(z._id)}
                  uebertragung={hierGeschrieben.has(z._id) ? { stand: "übertragen", grund: null } : null} />
              ))}
            {offen.map((w) => (
              <Nachricht key={w.meldungsId} kanalId={kanalId} online={online} eigen darfEntfernen={false} onEntfernen={() => undefined}
                lokal uebertragung={{ stand: w.stand, grund: w.grund }} datei={w.datei ?? null}
                z={{ _id: w.meldungsId, Text: w.daten["Text"] ?? null, Zeitpunkt: new Date(w.geraetezeit), Status: "sichtbar",
                  Anhang: w.daten["Anhang"] ?? null, Verfasser: ich.daten?.["Mitglied"] ?? null, Rolle: null }} />
            ))}
        </Scrollbereich>
      </div>
      <Abweisung grund={abweisung} />
      <div className="eingabe handlungen" role="region" aria-label="Eingabe">
        {standortOffen ? null : textfeld}
        <Kachel symbol="senden" begriff="Senden" art="kompakt" className="eingabe__senden" onPress={() => void senden()} />
        <Kachel symbol="anhang" begriff="Anhang" art="kompakt" className="handlungen__rest" onPress={() => setAnhangOffen(true)} />
        {aufnahme
          ? <Kachel symbol="anhalten" begriff="Anhalten" art="kompakt" onPress={() => { aufnahme.stop(); setAufnahme(null); }} />
          : <Kachel symbol="sprachnachricht" begriff="Sprachnachricht" art="kompakt" className="handlungen__rest" onPress={() => void aufnehmen()} />}
        <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="handlungen__rest" onPress={zurueckZuDenKanaelen} />
        <Kachel symbol="mehr" begriff="mehr" art="kompakt" className="handlungen__mehr" onPress={() => setMehrOffen(true)} />
        {vorgemerkt ? <span className="eingabe__anhang"><Symbol name="anhang" />{`${vorgemerkt.medienart} · ${vorgemerkt.name}`}</span> : null}
        <input ref={dateiwahl} type="file" hidden onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setVorgemerkt({ medienart: gewaehlteArt.current, datei: f, name: f.name });
        }} />
      </div>
      <Dialog name="mehr" symbol="mehr" offen={mehrOffen} onSchliessen={() => setMehrOffen(false)}>
        <div className="raster raster--2 handlungen__dialog">
          <Kachel symbol="anhang" begriff="Anhang" art="kompakt" onPress={() => { setMehrOffen(false); setAnhangOffen(true); }} />
          {aufnahme ? null : <Kachel symbol="sprachnachricht" begriff="Sprachnachricht" art="kompakt" onPress={() => { setMehrOffen(false); void aufnehmen(); }} />}
          <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" onPress={() => { setMehrOffen(false); zurueckZuDenKanaelen(); }} />
        </div>
        <div className="knopfreihe">
          <Knopf art="zweit" onPress={() => setMehrOffen(false)}>Schließen</Knopf>
        </div>
      </Dialog>
      <Dialog name="Anhang" symbol="anhang" offen={anhangOffen} onSchliessen={() => setAnhangOffen(false)}>
        <div className="knopfreihe">
          <Kachel symbol="foto" begriff="Foto" onPress={() => dateiWaehlen("Foto")} />
          <Kachel symbol="video" begriff="Video" onPress={() => dateiWaehlen("Video")} />
          <Kachel symbol="dokument" begriff="Dokument" onPress={() => dateiWaehlen("Dokument")} />
          <Kachel symbol="standort" begriff="Standort" onPress={() => { setAnhangOffen(false); setStandortOffen(true); }} />
          <Kachel symbol="schliessen" begriff="Schließen" onPress={() => setAnhangOffen(false)} />
        </div>
      </Dialog>
      <Dialog name="Standort" symbol="standort" offen={standortOffen} onSchliessen={() => setStandortOffen(false)}>
        <div className="knopfreihe">
          <Kachel symbol="standort" begriff="GPS-Messung" onPress={gpsMessung} />
          <Kachel symbol="karte" begriff="Karte" onPress={() => { setKarte(true); setPosition(null); }} />
        </div>
        {position && !karte ? <span className="zeitstempel">{`${position.Breite.toFixed(6)}, ${position.Länge.toFixed(6)}`}</span> : null}
        {karte ? (
          <TextField className="feld-text" value={positionText} onChange={setPositionText}>
            <Label className="feld-text__b">Position</Label>
            <Input inputMode="decimal" />
          </TextField>
        ) : null}
        {textfeld}
        <Kachel symbol="senden" begriff="Senden" onPress={() => void standortSenden()} isDisabled={!(karte ? parsePosition(positionText) : position)} />
      </Dialog>
      <Dialog name="Entfernen" symbol="schliessen" offen={entfernen !== null} onSchliessen={() => setEntfernen(null)} bestaetigung>
        <div className="knopfreihe">
          <Kachel symbol="haken" begriff="Bestätigen" onPress={() => entfernen && void zurueckziehen(entfernen)} />
          <Kachel symbol="schliessen" begriff="Abbrechen" onPress={() => setEntfernen(null)} />
        </div>
      </Dialog>
    </>
  );

  if (dj && rahmen) return <>{rahmen({ drueckjagdId: dj, name, art: "kanal", zurueck: false, children: inhalt })}</>;
  // Ohne Zustandsband zeigt der Kanal einer Drückjagd selbst, seit wann das Gerät keinen Kontakt hat (oberflaeche.md §9):
  // was im Verlauf steht, ist dann womöglich nicht mehr aktuell.
  const weg = kanal.daten?.["Drückjagd"] ? kontakt.keinKontaktSeit : null;
  return (
    <Ansicht name={name} symbol="nachricht" art="kanal">
      {weg ? <Status symbol="offline">{`kein Kontakt seit ${berlinUhrzeit(weg)}`}</Status> : null}
      {inhalt}
    </Ansicht>
  );
}
